import clearLocalSession from '@/auth/session'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  refreshPromise = null

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        const { config, response } = error
        if (!config || response?.status !== 401 || config.skipAuthRefresh) {
          return Promise.reject(error)
        }

        if (!this.getToken() || config.authRetry) {
          this.clearSession()
          return Promise.reject(error)
        }

        config.authRetry = true
        if (!this.refreshPromise) {
          this.refreshPromise = this.refreshToken()
            .then(({ data }) => {
              if (typeof data?.accessToken !== 'string' || data.accessToken.trim() === '') {
                throw new Error('Refresh response does not contain an access token')
              }
              this.setToken(data.accessToken)
              this.setRefreshToken(data.refreshToken ?? '')
              return data.accessToken
            })
            .catch(refreshError => {
              this.clearSession()
              throw refreshError
            })
            .finally(() => {
              this.refreshPromise = null
            })
        }

        return this.refreshPromise.then(accessToken => {
          config.headers = config.headers || {}
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
          return this.axiosIns(config)
        })
      },
    )
  }

  clearSession() {
    clearLocalSession(this.jwtConfig)
    window.location.href = '/login'
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    }, { skipAuthRefresh: true })
  }
}
